import api from 'api/instance';
import { washHub } from 'store';

import { FetchQueryType, FetchResponse } from 'types/common';
import { CollectionPoint } from 'types/location';
import { ReportActivity } from 'types/reportActivity';
import {
  CreateImpactReportPayload,
  CreateHarvestSummaryPayload,
  ImpactReport,
  Report,
} from 'types/report';
import { toMidnightISOString } from 'utils/dateUtils';

const URL = `${process.env.REACT_APP_API_URL}`;

type ParamsType = Omit<FetchQueryType, 'washHub'> & {
  site?: string[];
  accountManagers?: string[];
  generateImpact?: boolean;
  startDate?: Date;
  endDate?: Date;
  limit?: number;
  isArchived?: boolean;
  washHubId?: string;
  status?: number | number[];
  sort?: number;
  sortByField?: string;
  isSentToClient?: boolean;
  feeAdjustmentTypes?: string[];
  search?: string;
};

type CollectionPointsResponse = {
  items: CollectionPoint[];
  message: string;
};

export const fetchCollectionPoints = async (washHub?: string) => {
  const { data } = await api.get<CollectionPointsResponse>(
    `${URL}/location/list`,
    {
      params: {
        type: 'site',
        washHub,
        limit: 1000,
        activeOnly: 1,
      },
    },
  );

  return data?.items;
};

export const fetchHarvestSummarys = async (
  params: ParamsType,
  /** Aborts the request once react-query no longer needs it (e.g. a newer search). */
  signal?: AbortSignal,
) => {
  const endDate = toMidnightISOString(params.endDate);
  const startDate = toMidnightISOString(params.startDate);
  const { status } = params;

  const { data } = await api.get<FetchResponse<Report>>(`${URL}/report/list`, {
    params: {
      washHub: params.washHubId,
      startDate,
      endDate,
      site: (params.site || []).length > 0 ? params.site : undefined,
      ...(params.accountManagers && params.accountManagers.length
        ? { accountManagers: params.accountManagers }
        : {}),
      ...(typeof params.isSentToClient === 'boolean'
        ? { isSentToClient: params.isSentToClient }
        : {}),
      ...(params.feeAdjustmentTypes && params.feeAdjustmentTypes.length
        ? { feeAdjustmentTypes: params.feeAdjustmentTypes }
        : {}),
      page: params.page,
      limit: params.limit,
      generateImpact: params.generateImpact,
      sortByField: params.sortByField,
      sort: params.sort,

      ...(params.isArchived ? { archived: true } : null),
      ...(status !== undefined
        ? Array.isArray(status)
          ? { status: status }
          : { status: [status] }
        : null),
      ...(params.search ? { search: params.search } : null),
    },
    signal,
  });

  return data;
};

export const fetchHarvestSummaryCsvData = async (params: ParamsType) => {
  const endDate = toMidnightISOString(params.endDate);
  const startDate = toMidnightISOString(params.startDate);
  const { status } = params;

  const { data } = await api.post<FetchResponse<Report>>(
    `${URL}/report/export-harvest-reports`,
    {
      washHub: params.washHubId,
      startDate,
      endDate,
      site: params.site || undefined,
      ...(params.accountManagers && params.accountManagers.length
        ? { accountManagers: params.accountManagers }
        : {}),
      ...(typeof params.isSentToClient === 'boolean'
        ? { isSentToClient: params.isSentToClient }
        : {}),
      page: params.page,
      limit: params.limit,
      generateImpact: params.generateImpact,

      ...(params.isArchived && { archived: true }),
      ...(status !== undefined && {
        status: Array.isArray(status) ? status : [status],
      }),
    },
  );
  return data;
};

export const createHarvestSummary = async (
  payload: CreateHarvestSummaryPayload,
) => {
  const { data } = await api.post(
    `${URL}/report/generate-inventory-report`,
    payload,
  );

  return data;
};

export const fetchImpactReports = async (params: ParamsType) => {
  const endDate = toMidnightISOString(params.endDate);
  const startDate = toMidnightISOString(params.startDate);

  const { data } = await api.get<FetchResponse<ImpactReport>>(
    `${URL}/report/impact/list`,
    {
      params: {
        washHub: params.washHubId,
        startDate,
        endDate,
        site: (params.site || []).length > 0 ? params.site : undefined,
        ...(params.accountManagers && params.accountManagers.length
          ? { accountManagers: params.accountManagers }
          : {}),
        page: params.page,
        limit: params.limit,
        generateImpact: params.generateImpact,
        ...(params.sort && { sort: params.sort }),
        ...(params.sortByField && { sortByField: params.sortByField }),
      },
    },
  );

  return data;
};

export const createImpactReport = async (
  payload: CreateImpactReportPayload,
) => {
  const { id, ..._payload } = payload;

  const { data } = await api.post(
    `${URL}/report/create/impact/${id}`,
    _payload,
  );

  return data;
};

export const editHarvestSummary = async (
  payload: CreateHarvestSummaryPayload & { id: string },
) => {
  const { id, ..._payload } = payload;

  const { data } = await api.patch(`${URL}/report/edit/${id}`, _payload);

  return data;
};

export const editHarvestSummaryInventoryCount = async (
  payload: CreateHarvestSummaryPayload & { id: string },
) => {
  const { id, site, ..._payload } = payload;

  const { data } = await api.patch(
    `${URL}/report/edit/inventory-report/${id}`,
    _payload,
  );

  return data;
};

export const editHarvestSummaryStatus = async (payload: {
  reportId: string;
  status: number;
  cokeAdjustment?: number;
  feeAdjustment?: number;
  feeAdjustments?: Array<{ type: string; amount: number }>;
  invoiceNotes?: string;
  summaryNotes?: string;
  isSentToClient?: boolean;
}) => {
  const { data } = await api.patch(`${URL}/report/update-status`, payload);

  return data;
};

export const editImpactReport = async (payload: CreateImpactReportPayload) => {
  const { id, ..._payload } = payload;

  const { data } = await api.patch(`${URL}/report/impact/edit/${id}`, _payload);

  return data;
};

export const deleteInventoryReport = async (id: string) => {
  const { data } = await api.delete(`${URL}/report/delete/${id}`);

  return data;
};

export const deleteImpactReport = async (id: string) => {
  const { data } = await api.delete(`${URL}/report/impact/delete/${id}`);

  return data;
};

export const generateImpactReportCSV = async (payload: {
  site?: string[];
  accountManagers?: string[];
  sort?: number;
  page?: number;
  limit?: number;
  generateImpact: boolean;
  startDate?: Date;
  endDate?: Date;
  washHub: string;
}) => {
  const endDate = toMidnightISOString(payload.endDate);
  const startDate = toMidnightISOString(payload.startDate);

  const { data } = await api.post(`${URL}/report/export-impact-reports`, {
    ...payload,
    startDate,
    endDate,
  });

  return data;
};

export const generateHarvestSummaryReport = async (payload: any) => {
  const { data } = await api.post('/report/generate-inventory-report', payload);

  return data;
};

export const manualRefreshTrigger = async () => {
  const { data } = await api.post('/report/trigger-recalculations');

  return data;
};

export const manualRefreshTriggerById = async (id: string) => {
  const { data } = await api.post(`/report/trigger-recalculations/${id}`);
  return data;
};
export const reportArchive = async (id: string) => {
  const { data } = await api.patch(
    `report/toggle-harvest-report-archive/${id}`,
  );
  return data;
};

export const getReportCounts = async (params: ParamsType) => {
  const endDate = toMidnightISOString(params.endDate);
  const startDate = toMidnightISOString(params.startDate);
  const { status } = params;

  const { data } = await api.get(`${URL}/report/get-counts`, {
    params: {
      washHub: params.washHubId,
      startDate,
      endDate,
      site: (params.site || []).length > 0 ? params.site : undefined,
      accountManagers:
        (params.accountManagers || []).length > 0
          ? params.accountManagers
          : undefined,
      ...(typeof params.isSentToClient === 'boolean'
        ? { isSentToClient: params.isSentToClient }
        : {}),
      page: params.page,
      limit: params.limit,
      generateImpact: params.generateImpact,
      sortByField: params.sortByField,
      sort: params.sort,

      ...(params.isArchived ? { archived: true } : null),
      ...(status !== undefined
        ? Array.isArray(status)
          ? { status: status }
          : { status: [status] }
        : null),
    },
  });

  return data?.item;
};

export const fetchReportActivityHistory = async (
  reportId: string,
  page: number = 1,
  limit: number = 10,
) => {
  try {
    const { data } = await api.get<{
      success: boolean;
      message: string;
      item: ReportActivity[];
      total: number;
      totalPages: number;
      currentPage: number;
    }>(`${URL}/report/${reportId}/activity-history`, {
      params: {
        page,
        limit,
      },
    });

    return data;
  } catch (error) {
    // Return empty response instead of throwing error
    return {
      success: false,
      message: 'Failed to fetch activity history',
      item: [],
      total: 0,
      currentPage: page,
      totalPages: 1,
    };
  }
};
